import type { CfbSchoolCatalogEntry } from '../cfbSchoolCatalogTypes';
import {
  buildCfbCatalogIndex,
  resolveSchoolBrandingUrls,
  resolveSchoolFromIndex,
  type CfbCatalogIndex,
} from './cfbTeamVisualIndex';
import {
  contrastRatio,
  resolveTeamAccentColor,
  type AccentColorSource,
} from './accentColorResolver';
import { resolveSchoolAccentColor } from './cfbSchoolAccent';

export type TeamVisualKind =
  | 'cfb_icon'
  | 'cfb_primary'
  | 'initials'
  | 'placeholder';

export type TeamVisual = {
  displayName: string;
  abbreviation: string;
  initials: string;
  primaryColor: string | null;
  secondaryColor: string | null;
  accentColor: string;
  accentColorSource: AccentColorSource;
  schoolId: string | null;
  /**
   * Set only when a CFB school record is marked `logo_status === 'licensed'`.
   * A team image renders only in that case. NFL teams and unmatched labels stay null.
   */
  badgeAssetUrl: string | null;
  primaryLogoUrl: string | null;
  kind: TeamVisualKind;
  /** Tile fill for the new matchup art. Existing chips do not read this field. */
  tileColors: TeamTileColors | null;
};

export type TeamTileColors = {
  primary: string;
  onPrimary: string;
  secondary: string;
  onSecondary: string;
};

export const TILE_ON_PRIMARY_LIGHT = '#FFFFFF';
export const TILE_ON_PRIMARY_DARK = '#101820';
const TILE_ON_PRIMARY_MIN_CONTRAST = 3;

/** Large-text ink on a tile fill. Picks whichever of white or #101820 clears 3:1. */
export function pickTileOnPrimary(primary: string): '#FFFFFF' | '#101820' {
  const light = contrastRatio(TILE_ON_PRIMARY_LIGHT, primary);
  const dark = contrastRatio(TILE_ON_PRIMARY_DARK, primary);
  if (light >= TILE_ON_PRIMARY_MIN_CONTRAST && light >= dark) {
    return TILE_ON_PRIMARY_LIGHT;
  }
  return TILE_ON_PRIMARY_DARK;
}

function normalizeHex(value: string | null | undefined): string | null {
  const normalized = (value ?? '').trim().toUpperCase();
  return /^#[0-9A-F]{6}$/.test(normalized) ? normalized : null;
}

export function tileColorsFromPrimary(
  primary: string | null | undefined,
  secondary?: string | null
): TeamTileColors | null {
  const normalized = normalizeHex(primary);
  if (!normalized) {
    return null;
  }
  const secondaryHex = normalizeHex(secondary) ?? normalized;
  return {
    primary: normalized,
    onPrimary: pickTileOnPrimary(normalized),
    secondary: secondaryHex,
    onSecondary: pickTileOnPrimary(secondaryHex),
  };
}

export type ResolveTeamVisualInput = {
  label: string;
  gameKey?: string | null;
  schoolId?: string | null;
  variant?: 'badge' | 'primary';
  cfbCatalogIndex?: CfbCatalogIndex | null;
  cfbSchools?: CfbSchoolCatalogEntry[] | null;
};

const SLATE_PRIMARY = '#334155';
const SLATE_SECONDARY = '#f8fafc';

export type TileColorsSource = {
  /** 1 official HEX/RGB, 2 Pantone sRGB from pantone.com, 3 widely used reference. */
  tier: 1 | 2 | 3;
  url: string;
  verified: boolean;
};

export type NflTeamRecord = {
  abbrev: string;
  nickname: string;
  city: string;
  aliases: string[];
  /** Tile fill. Existing chips do not read this or tileColorsSource. */
  tileColors: TeamTileColors;
  /** Provenance for tileColors.primary. */
  tileColorsSource: TileColorsSource;
  /** Provenance for tileColors.secondary. */
  tileSecondarySource: TileColorsSource;
};

const BEARS_BRAND_URL =
  'https://static.clubs.nfl.com/image/upload/v1723751161/bears/klb6g0rcaceaoswmuytq.pdf';
const RAMS_BRAND_URL =
  'https://stage-static.clubs.nfl.com/image/upload/browns/fw6klbpa6smlcmbemfhx.pdf';
const COMMANDERS_BRAND_URL =
  'https://media.washington.1rmg.com/wp-content/uploads/2022/02/03143736/WASHINGTON_LOGO-GUIDE.pdf';
const TEAM_COLOR_REFERENCE_URL = 'https://teamcolorcodes.com/nfl-team-color-codes/';

function officialSource(url: string): TileColorsSource {
  return { tier: 1, url, verified: true };
}

function referenceSource(): TileColorsSource {
  return { tier: 3, url: TEAM_COLOR_REFERENCE_URL, verified: false };
}

export const NFL_TEAM_CATALOG: readonly NflTeamRecord[] = [
  { abbrev: 'ARI', nickname: 'Cardinals', city: 'Arizona', aliases: ['arizona cardinals', 'az cardinals'], tileColors: { primary: '#97233F', onPrimary: '#FFFFFF', secondary: '#000000', onSecondary: '#FFFFFF' }, tileSecondarySource: referenceSource(), tileColorsSource: referenceSource() },
  { abbrev: 'ATL', nickname: 'Falcons', city: 'Atlanta', aliases: ['atlanta falcons'], tileColors: { primary: '#A71930', onPrimary: '#FFFFFF', secondary: '#000000', onSecondary: '#FFFFFF' }, tileSecondarySource: referenceSource(), tileColorsSource: referenceSource() },
  { abbrev: 'BAL', nickname: 'Ravens', city: 'Baltimore', aliases: ['baltimore ravens'], tileColors: { primary: '#241773', onPrimary: '#FFFFFF', secondary: '#000000', onSecondary: '#FFFFFF' }, tileSecondarySource: referenceSource(), tileColorsSource: referenceSource() },
  { abbrev: 'BUF', nickname: 'Bills', city: 'Buffalo', aliases: ['buffalo bills'], tileColors: { primary: '#00338D', onPrimary: '#FFFFFF', secondary: '#C60C30', onSecondary: '#FFFFFF' }, tileSecondarySource: referenceSource(), tileColorsSource: referenceSource() },
  { abbrev: 'CAR', nickname: 'Panthers', city: 'Carolina', aliases: ['carolina panthers'], tileColors: { primary: '#0085CA', onPrimary: '#101820', secondary: '#101820', onSecondary: '#FFFFFF' }, tileSecondarySource: referenceSource(), tileColorsSource: referenceSource() },
  { abbrev: 'CHI', nickname: 'Bears', city: 'Chicago', aliases: ['chicago bears'], tileColors: { primary: '#0B162A', onPrimary: '#FFFFFF', secondary: '#C83803', onSecondary: '#FFFFFF' }, tileSecondarySource: referenceSource(), tileColorsSource: officialSource(BEARS_BRAND_URL) },
  { abbrev: 'CIN', nickname: 'Bengals', city: 'Cincinnati', aliases: ['cincinnati bengals'], tileColors: { primary: '#FB4F14', onPrimary: '#101820', secondary: '#000000', onSecondary: '#FFFFFF' }, tileSecondarySource: referenceSource(), tileColorsSource: referenceSource() },
  { abbrev: 'CLE', nickname: 'Browns', city: 'Cleveland', aliases: ['cleveland browns'], tileColors: { primary: '#311D00', onPrimary: '#FFFFFF', secondary: '#FF3C00', onSecondary: '#101820' }, tileSecondarySource: referenceSource(), tileColorsSource: referenceSource() },
  { abbrev: 'DAL', nickname: 'Cowboys', city: 'Dallas', aliases: ['dallas cowboys'], tileColors: { primary: '#003594', onPrimary: '#FFFFFF', secondary: '#041E42', onSecondary: '#FFFFFF' }, tileSecondarySource: referenceSource(), tileColorsSource: referenceSource() },
  { abbrev: 'DEN', nickname: 'Broncos', city: 'Denver', aliases: ['denver broncos'], tileColors: { primary: '#FB4F14', onPrimary: '#101820', secondary: '#002244', onSecondary: '#FFFFFF' }, tileSecondarySource: referenceSource(), tileColorsSource: referenceSource() },
  { abbrev: 'DET', nickname: 'Lions', city: 'Detroit', aliases: ['detroit lions'], tileColors: { primary: '#0076B6', onPrimary: '#FFFFFF', secondary: '#B0B7BC', onSecondary: '#101820' }, tileSecondarySource: referenceSource(), tileColorsSource: referenceSource() },
  { abbrev: 'GB', nickname: 'Packers', city: 'Green Bay', aliases: ['green bay packers', 'gnb'], tileColors: { primary: '#203731', onPrimary: '#FFFFFF', secondary: '#FFB612', onSecondary: '#101820' }, tileSecondarySource: referenceSource(), tileColorsSource: referenceSource() },
  { abbrev: 'HOU', nickname: 'Texans', city: 'Houston', aliases: ['houston texans'], tileColors: { primary: '#03202F', onPrimary: '#FFFFFF', secondary: '#A71930', onSecondary: '#FFFFFF' }, tileSecondarySource: referenceSource(), tileColorsSource: referenceSource() },
  { abbrev: 'IND', nickname: 'Colts', city: 'Indianapolis', aliases: ['indianapolis colts', 'indy colts'], tileColors: { primary: '#002C5F', onPrimary: '#FFFFFF', secondary: '#A2AAAD', onSecondary: '#101820' }, tileSecondarySource: referenceSource(), tileColorsSource: referenceSource() },
  { abbrev: 'JAX', nickname: 'Jaguars', city: 'Jacksonville', aliases: ['jacksonville jaguars', 'jac'], tileColors: { primary: '#101820', onPrimary: '#FFFFFF', secondary: '#D7A22A', onSecondary: '#101820' }, tileSecondarySource: referenceSource(), tileColorsSource: referenceSource() },
  { abbrev: 'KC', nickname: 'Chiefs', city: 'Kansas City', aliases: ['kansas city chiefs'], tileColors: { primary: '#E31837', onPrimary: '#FFFFFF', secondary: '#FFB81C', onSecondary: '#101820' }, tileSecondarySource: referenceSource(), tileColorsSource: referenceSource() },
  { abbrev: 'LV', nickname: 'Raiders', city: 'Las Vegas', aliases: ['las vegas raiders', 'oakland raiders', 'oak'], tileColors: { primary: '#000000', onPrimary: '#FFFFFF', secondary: '#A5ACAF', onSecondary: '#101820' }, tileSecondarySource: referenceSource(), tileColorsSource: referenceSource() },
  { abbrev: 'LAC', nickname: 'Chargers', city: 'Los Angeles', aliases: ['los angeles chargers', 'la chargers', 'sd chargers'], tileColors: { primary: '#0080C6', onPrimary: '#FFFFFF', secondary: '#FFC20E', onSecondary: '#101820' }, tileSecondarySource: referenceSource(), tileColorsSource: referenceSource() },
  { abbrev: 'LAR', nickname: 'Rams', city: 'Los Angeles', aliases: ['los angeles rams', 'la rams', 'stl rams'], tileColors: { primary: '#003594', onPrimary: '#FFFFFF', secondary: '#FFA300', onSecondary: '#101820' }, tileSecondarySource: referenceSource(), tileColorsSource: officialSource(RAMS_BRAND_URL) },
  { abbrev: 'MIA', nickname: 'Dolphins', city: 'Miami', aliases: ['miami dolphins'], tileColors: { primary: '#008E97', onPrimary: '#101820', secondary: '#FC4C02', onSecondary: '#101820' }, tileSecondarySource: referenceSource(), tileColorsSource: referenceSource() },
  { abbrev: 'MIN', nickname: 'Vikings', city: 'Minnesota', aliases: ['minnesota vikings'], tileColors: { primary: '#4F2683', onPrimary: '#FFFFFF', secondary: '#FFC62F', onSecondary: '#101820' }, tileSecondarySource: referenceSource(), tileColorsSource: referenceSource() },
  { abbrev: 'NE', nickname: 'Patriots', city: 'New England', aliases: ['new england patriots', 'nwe'], tileColors: { primary: '#002244', onPrimary: '#FFFFFF', secondary: '#C60C30', onSecondary: '#FFFFFF' }, tileSecondarySource: referenceSource(), tileColorsSource: referenceSource() },
  { abbrev: 'NO', nickname: 'Saints', city: 'New Orleans', aliases: ['new orleans saints'], tileColors: { primary: '#D3BC8D', onPrimary: '#101820', secondary: '#101820', onSecondary: '#FFFFFF' }, tileSecondarySource: referenceSource(), tileColorsSource: referenceSource() },
  { abbrev: 'NYG', nickname: 'Giants', city: 'New York', aliases: ['new york giants', 'ny giants'], tileColors: { primary: '#0B2265', onPrimary: '#FFFFFF', secondary: '#A71930', onSecondary: '#FFFFFF' }, tileSecondarySource: referenceSource(), tileColorsSource: referenceSource() },
  { abbrev: 'NYJ', nickname: 'Jets', city: 'New York', aliases: ['new york jets', 'ny jets'], tileColors: { primary: '#125740', onPrimary: '#FFFFFF', secondary: '#000000', onSecondary: '#FFFFFF' }, tileSecondarySource: referenceSource(), tileColorsSource: referenceSource() },
  { abbrev: 'PHI', nickname: 'Eagles', city: 'Philadelphia', aliases: ['philadelphia eagles'], tileColors: { primary: '#004C54', onPrimary: '#FFFFFF', secondary: '#A5ACAF', onSecondary: '#101820' }, tileSecondarySource: referenceSource(), tileColorsSource: referenceSource() },
  { abbrev: 'PIT', nickname: 'Steelers', city: 'Pittsburgh', aliases: ['pittsburgh steelers'], tileColors: { primary: '#FFB612', onPrimary: '#101820', secondary: '#101820', onSecondary: '#FFFFFF' }, tileSecondarySource: referenceSource(), tileColorsSource: referenceSource() },
  { abbrev: 'SF', nickname: '49ers', city: 'San Francisco', aliases: ['san francisco 49ers', 'niners', 'sfo'], tileColors: { primary: '#AA0000', onPrimary: '#FFFFFF', secondary: '#B3995D', onSecondary: '#101820' }, tileSecondarySource: referenceSource(), tileColorsSource: referenceSource() },
  { abbrev: 'SEA', nickname: 'Seahawks', city: 'Seattle', aliases: ['seattle seahawks'], tileColors: { primary: '#002244', onPrimary: '#FFFFFF', secondary: '#69BE28', onSecondary: '#101820' }, tileSecondarySource: referenceSource(), tileColorsSource: referenceSource() },
  { abbrev: 'TB', nickname: 'Buccaneers', city: 'Tampa Bay', aliases: ['tampa bay buccaneers', 'bucs', 'tbb'], tileColors: { primary: '#D50A0A', onPrimary: '#FFFFFF', secondary: '#FF7900', onSecondary: '#101820' }, tileSecondarySource: referenceSource(), tileColorsSource: referenceSource() },
  { abbrev: 'TEN', nickname: 'Titans', city: 'Tennessee', aliases: ['tennessee titans'], tileColors: { primary: '#0C2340', onPrimary: '#FFFFFF', secondary: '#4B92DB', onSecondary: '#101820' }, tileSecondarySource: referenceSource(), tileColorsSource: referenceSource() },
  { abbrev: 'WAS', nickname: 'Commanders', city: 'Washington', aliases: ['washington commanders', 'washington football team', 'redskins', 'wsh'], tileColors: { primary: '#5A1414', onPrimary: '#FFFFFF', secondary: '#FFB612', onSecondary: '#101820' }, tileSecondarySource: referenceSource(), tileColorsSource: officialSource(COMMANDERS_BRAND_URL) },
];

const NFL_TEAM_LOOKUP = new Map<string, NflTeamRecord>();

for (const team of NFL_TEAM_CATALOG) {
  const keys = [
    team.abbrev,
    team.nickname,
    `${team.city} ${team.nickname}`,
    ...team.aliases,
  ];
  for (const key of keys) {
    NFL_TEAM_LOOKUP.set(normalizeTeamLabel(key), team);
  }
}

/** True when both labels are the same NFL team, including a scorebug abbreviation. */
export function nflTeamsMatch(left: string, right: string): boolean {
  const a = NFL_TEAM_LOOKUP.get(normalizeTeamLabel(left));
  const b = NFL_TEAM_LOOKUP.get(normalizeTeamLabel(right));
  return Boolean(a && b && a.abbrev === b.abbrev);
}

/** "Giants" and "NYG" both read "New York Giants". An unknown label stays as written. */
export function nflCanonicalName(label: string | null | undefined): string {
  const trimmed = (label ?? '').trim();
  if (!trimmed) return '';
  const matched = NFL_TEAM_LOOKUP.get(normalizeTeamLabel(trimmed));
  if (!matched) return trimmed;
  return `${matched.city} ${matched.nickname}`;
}

function normalizeTeamLabel(label: string): string {
  return label
    .trim()
    .toLowerCase()
    .replace(/[^\w\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function buildInitials(displayName: string): string {
  const parts = displayName.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0] ?? ''}${parts[parts.length - 1][0] ?? ''}`.toUpperCase();
  }
  return displayName.slice(0, 2).toUpperCase();
}

function resolveAccentFromSchool(school: {
  primary_color?: string | null;
  secondary_color?: string | null;
  tertiary_color?: string | null;
  quaternary_color?: string | null;
  colors_verified?: boolean;
}) {
  return resolveSchoolAccentColor({
    primary_color: school.primary_color,
    secondary_color: school.secondary_color,
    tertiary_color: school.tertiary_color,
    quaternary_color: school.quaternary_color,
    colors_verified: Boolean(school.colors_verified),
  });
}

function resolveAccentFromColors(input: {
  primaryColor?: string | null;
  secondaryColor?: string | null;
  tertiaryColor?: string | null;
  quaternaryColor?: string | null;
}) {
  return resolveTeamAccentColor({
    primaryColor: input.primaryColor,
    secondaryColor: input.secondaryColor,
    tertiaryColor: input.tertiaryColor,
    quaternaryColor: input.quaternaryColor,
  });
}

function resolveCfbVisual(
  input: ResolveTeamVisualInput,
  index: CfbCatalogIndex | null
): TeamVisual {
  const label = input.label.trim();
  const school = resolveSchoolFromIndex(index, {
    schoolId: input.schoolId,
    label,
  });

  if (!school) {
    const initials = buildInitials(label || 'TM');
    const accent = resolveAccentFromColors({});
    return {
      displayName: label || 'Team',
      abbreviation: initials,
      initials,
      primaryColor: null,
      secondaryColor: null,
      accentColor: accent.accentColor,
      accentColorSource: accent.source,
      schoolId: null,
      badgeAssetUrl: null,
      primaryLogoUrl: null,
      kind: 'placeholder',
      tileColors: null,
    };
  }

  const branding = resolveSchoolBrandingUrls(school);
  const displayName = label || school.athletic_name;
  const variant = input.variant ?? 'badge';
  const accent = resolveAccentFromSchool(school);

  // A team image renders only when a record is marked logo_status === 'licensed'.
  if (school.logo_status === 'licensed' && branding) {
    const badgeUrl = branding.icon_url;
    const primaryUrl = branding.primary_url;
    return {
      displayName,
      abbreviation: school.initials,
      initials: school.initials,
      primaryColor: school.colors_verified ? school.primary_color ?? null : null,
      secondaryColor: school.colors_verified ? school.secondary_color ?? null : null,
      accentColor: accent.accentColor,
      accentColorSource: accent.source,
      schoolId: school.school_id,
      badgeAssetUrl: badgeUrl,
      primaryLogoUrl: primaryUrl,
      kind: variant === 'primary' ? 'cfb_primary' : 'cfb_icon',
      tileColors: tileColorsFromPrimary(school.primary_color, school.secondary_color),
    };
  }

  return {
    displayName,
    abbreviation: school.initials,
    initials: school.initials,
    primaryColor: school.colors_verified ? school.primary_color ?? SLATE_PRIMARY : null,
    secondaryColor: school.colors_verified ? school.secondary_color ?? SLATE_SECONDARY : null,
    accentColor: accent.accentColor,
    accentColorSource: accent.source,
    schoolId: school.school_id,
    badgeAssetUrl: null,
    primaryLogoUrl: null,
    kind: 'initials',
    tileColors: tileColorsFromPrimary(school.primary_color, school.secondary_color),
  };
}

function resolveNflVisual(label: string): TeamVisual {
  const trimmed = label.trim();
  const normalized = normalizeTeamLabel(trimmed);
  const matched = NFL_TEAM_LOOKUP.get(normalized);

  if (matched) {
    const accent = resolveAccentFromColors({});
    return {
      displayName: trimmed || `${matched.city} ${matched.nickname}`,
      abbreviation: matched.abbrev,
      initials: matched.abbrev,
      primaryColor: null,
      secondaryColor: null,
      accentColor: accent.accentColor,
      accentColorSource: accent.source,
      schoolId: null,
      badgeAssetUrl: null,
      primaryLogoUrl: null,
      kind: 'initials',
      tileColors: matched.tileColors,
    };
  }

  const initials = buildInitials(trimmed || 'TM');
  const accent = resolveAccentFromColors({});
  return {
    displayName: trimmed || 'Team',
    abbreviation: initials,
    initials,
    primaryColor: null,
    secondaryColor: null,
    accentColor: accent.accentColor,
    accentColorSource: accent.source,
    schoolId: null,
    badgeAssetUrl: null,
    primaryLogoUrl: null,
    kind: 'placeholder',
    tileColors: null,
  };
}

export function resolveTeamVisual(input: ResolveTeamVisualInput): TeamVisual {
  const gameKey = (input.gameKey ?? '').trim().toLowerCase();
  if (gameKey === 'cfb') {
    const index =
      input.cfbCatalogIndex ??
      (input.cfbSchools ? buildCfbCatalogIndex(input.cfbSchools) : null);
    return resolveCfbVisual(input, index);
  }
  const visual = resolveNflVisual(input.label);
  if (gameKey && gameKey !== 'madden') {
    return { ...visual, tileColors: null };
  }
  return visual;
}

/** @deprecated Use resolveTeamVisual({ label, gameKey: 'madden' }) */
export function resolveLeagueTeamVisual(label: string): TeamVisual {
  return resolveTeamVisual({ label, gameKey: 'madden' });
}

export type LeagueTeamVisual = TeamVisual;
